import { apiError } from "../../../../../../../utils/formErrors";
import React, { useState } from 'react';
import { Box, Stack, Radio, CircularProgress } from "@mui/material";
import { useOutletContext } from 'react-router';
import BtnPrimary from '../../../../../../../component/PrimaryButton';
import BtnSecondary from '../../../../../../../component/SecondaryButton';
import { updateEmployeeDetailsById } from '../../../../../../../services/employee';
import { useNotify } from '../../../../../../../component/NotificationProvider';
import { tokens, motion, radius } from '../../../../../../../theme';

const GENDERS = ["Male", "Female", "None"];
const GENDER_LABEL = { Male: "Male", Female: "Female", None: "Do not wish to identify" };

const ETHNICITIES = [
  "Hispanic or Latino: A person of Cuban, Mexican, Puerto Rican, South or Central American, or other Spanish culture or origin regardless of race.",
  "White (Not Hispanic or Latino): A person having origins in any of the original peoples of Europe, the Middle East or North Africa.",
  "Black or African American (Not Hispanic or Latino): A person having origins in any of the black racial groups of Africa.",
  "Native Hawaiian or Pacific Islander (Not Hispanic or Latino): A person having origins in any of the peoples of Hawaii, Guam, Samoa or other Pacific Islands.",
  "Asian (Not Hispanic or Latino): A person having origins in any of the original peoples of the Far East, Southeast Asia or the Indian Subcontinent, including, for example, Cambodia, China, India, Japan, Korea, Malaysia, Pakistan, the Philippine Islands, Thailand and Vietnam.",
  "I do not wish to disclose.",
];

const OptionRow = ({ label, selected, editing, onSelect }) => (
  <Stack direction="row" alignItems="flex-start" spacing={1.25}
    onClick={editing ? onSelect : undefined}
    sx={{
      p: "0.75rem 1rem", borderRadius: "10px",
      border: `1px solid ${selected ? tokens.accent : tokens.border}`,
      background: selected ? `${tokens.accentTint}` : tokens.card,
      cursor: editing ? "pointer" : "default", transition: `border-color 120ms ${motion.easing.standard}`,
      "&:hover": editing ? { borderColor: tokens.accent } : {},
    }}>
    <Radio checked={selected} disabled={!editing} size="small" sx={{ p: 0, mt: "0.25rem", color: tokens.border, "&.Mui-checked": { color: tokens.accent } }} />
    <Box sx={{ fontSize: "0.875rem", color: tokens.text }}>{label}</Box>
  </Stack>
);

const SelfIdentification = () => {
  const [employeeData] = useOutletContext();
  const notify = useNotify();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gender, setGender] = useState(employeeData?.gender || "");
  const [ethnicity, setEthnicity] = useState(employeeData?.ethinicity || "");

  const cancel = () => {
    setGender(employeeData?.gender || "");
    setEthnicity(employeeData?.ethinicity || "");
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateEmployeeDetailsById(employeeData?._id, { gender, ethinicity: ethnicity });
      if (res?.success === false) throw apiError(res, "Could not save");
      notify.success("Self-identification updated");
      setEditing(false);
    } catch (e) {
      notify.error(e?.message || "Could not save self-identification");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: "1rem" }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text }}>Self-Identification</Box>
        {editing ? (
          <Stack direction="row" spacing={1}>
            <BtnSecondary onClick={cancel} disabled={saving} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
            <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
              {saving ? <CircularProgress size={"1rem"} /> : "Save"}
            </BtnPrimary>
          </Stack>
        ) : (
          <BtnSecondary onClick={() => setEditing(true)} sx={{ borderRadius: "8px" }}>Edit</BtnSecondary>
        )}
      </Stack>

      <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mb: "1.5rem", maxWidth: "60rem" }}>
        Voluntary diversity & inclusion information, kept confidential and used only for accommodation
        purposes as required by law. A disability is a physical or mental impairment that substantially
        limits a major life activity, or a history of such.
      </Box>

      <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mb: "0.75rem" }}>Gender</Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: "2rem" }}>
        {GENDERS.map((g) => (
          <Box key={g} sx={{ flex: 1 }}>
            <OptionRow label={GENDER_LABEL[g]} selected={gender === g} editing={editing} onSelect={() => setGender(g)} />
          </Box>
        ))}
      </Stack>

      <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mb: "0.75rem" }}>Ethnicity</Box>
      <Stack spacing={1}>
        {ETHNICITIES.map((e) => (
          <OptionRow key={e} label={e} selected={ethnicity === e} editing={editing} onSelect={() => setEthnicity(e)} />
        ))}
      </Stack>
    </Box>
  );
};

export default SelfIdentification;
